import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { vectorStore, isUsingSupabase } from "@/lib/ai/vector-store-provider";
import type { Document } from "@/lib/ai/vector-store";
import { Search, Trash, Database, Info, X, AlertTriangle, Check, Cloud, HardDrive } from "lucide-react";

interface KnowledgeManagerProps {
  onClose: () => void;
}

export function KnowledgeManager({ onClose }: KnowledgeManagerProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Load all documents from the vector store
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const allDocs = await vectorStore.getAllDocuments();
      setDocuments(allDocs);
    } catch (error) {
      console.error("Error loading documents:", error);
      toast({
        variant: "destructive",
        title: "Error",
        message: "Failed to load documents from the knowledge base. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      // If empty query, show all documents
      loadDocuments();
      return;
    }

    setIsLoading(true);
    try {
      // Otherwise, perform a search using the vector store
      const results = await vectorStore.similaritySearch(searchQuery, 10);
      setDocuments(results);
    } catch (error) {
      console.error("Error searching documents:", error);
      toast({
        variant: "destructive",
        title: "Search failed",
        message: "Failed to search the knowledge base. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = (id: string) => {
    setSelectedDocId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedDocId) {
      try {
        const success = await vectorStore.removeDocument(selectedDocId);
        if (success) {
          // Update the local documents list
          setDocuments((prev) => prev.filter((doc) => doc.id !== selectedDocId));
          toast({
            title: "Document removed",
            message: "The document was removed from the knowledge base",
          });
        } else {
          throw new Error("Failed to remove document");
        }
      } catch (error) {
        console.error("Error removing document:", error);
        toast({
          variant: "destructive",
          title: "Error",
          message: "Failed to remove the document. Please try again.",
        });
      }
    }
    setIsDeleteDialogOpen(false);
  };

  const handleClearAll = async () => {
    try {
      await vectorStore.clearDocuments();
      setDocuments([]);
      toast({
        title: "Knowledge base cleared",
        message: "All documents have been removed from the knowledge base",
      });
    } catch (error) {
      console.error("Error clearing documents:", error);
      toast({
        variant: "destructive",
        title: "Error",
        message: "Failed to clear the knowledge base. Please try again.",
      });
    }
    setIsDeleteDialogOpen(false);
  };

  const getDocumentPreview = (content: string, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.slice(0, maxLength) + "...";
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Knowledge Management
            </CardTitle>
            <CardDescription>
              View and manage documents in the FlexHelp AI knowledge base
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search knowledge base..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="pl-9"
            />
          </div>
          <Button onClick={handleSearch}>Search</Button>
        </div>

        <div className="bg-muted/20 rounded-lg p-3 flex gap-2 items-center">
          <div className="rounded-full bg-primary/10 p-2">
            <Info className="h-4 w-4 text-primary" />
          </div>
          <div className="text-sm">
            <div className="font-medium">Knowledge base status</div>
            <div className="text-muted-foreground">
              {vectorStore.getDocumentCount()} document chunks available{" "}
              <div className="flex items-center gap-1 mt-1">
                {vectorStore.isUsingEmbeddings() ? (
                  <span className="text-green-500 flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Using OpenAI embeddings for semantic search
                  </span>
                ) : (
                  <span className="text-amber-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Using keyword matching (OpenAI embeddings not available)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 mt-1">
                {isUsingSupabase() ? (
                  <span className="text-blue-500 flex items-center gap-1">
                    <Cloud className="h-3 w-3" />
                    Using Supabase for persistent storage
                  </span>
                ) : (
                  <span className="text-orange-500 flex items-center gap-1">
                    <HardDrive className="h-3 w-3" />
                    Using in-memory storage (data will be lost on refresh)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-md">
          <div className="bg-muted/50 py-2 px-4 text-sm font-medium border-b flex justify-between">
            <span>Documents ({documents.length})</span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs text-destructive hover:text-destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
              disabled={documents.length === 0}
            >
              <Trash className="h-3 w-3 mr-1" />
              Clear All
            </Button>
          </div>
          <ScrollArea className="h-[300px]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mb-2"></div>
                <p>Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <Database className="h-12 w-12 mb-2 opacity-20" />
                <p>No documents found in knowledge base</p>
              </div>
            ) : (
              <div className="divide-y">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-3 hover:bg-muted/20 transition-colors"
                  >
                    <div className="flex justify-between">
                      <div className="text-xs text-muted-foreground mb-1">
                        ID: {doc.id}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 w-6 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        <Trash className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-sm">
                      {getDocumentPreview(doc.content)}
                    </div>
                    {doc.metadata && Object.keys(doc.metadata).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Metadata: {JSON.stringify(doc.metadata)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </CardContent>
      <CardFooter className="border-t pt-4 flex justify-between">
        <div className="text-xs text-muted-foreground">
          Search uses the same semantic search algorithm as FlexHelp AI.
        </div>
        <Button onClick={onClose}>Close</Button>
      </CardFooter>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedDocId ? "Delete document" : "Clear all documents"}
            </DialogTitle>
            <DialogDescription>
              {selectedDocId
                ? "Are you sure you want to delete this document? This action cannot be undone."
                : "Are you sure you want to clear all documents from the knowledge base? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={selectedDocId ? confirmDelete : handleClearAll}
            >
              {selectedDocId ? "Delete" : "Clear All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
} 